import MenuIcon from '@mui/icons-material/Menu'
import DescriptionIcon from '@mui/icons-material/Description'
import { Menu, MenuItem, IconButton, Tooltip, useMediaQuery, useTheme, Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import LeaveButton from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/LeaveButton'
import { getMmssString } from '../LiveSessionPageComponents'
import { LSVideoPartHeader, LSVideoPartHeaderButton } from '../LiveSessionPageStyles'
import { LiveSessionHeaderDetails } from './LiveSessionHeaderDetails'
import SessionTopTabs from './TopTabs/SessionTopTabs'

export default function LiveSessionHeaderComponent({
  setTimeElapsedInSeconds,
  timeElapsedInSeconds,
  bookedEventFinalData,
  auxiliaryData,
  userRole,
  selectedPlatform,
  handleLeaveRoom,
  changeLayoutFunction,
  setAnchorElForHeaderMenu,
  anchorElForHeaderMenu,
  setDrawerForMoreOptionsOpenMobile,
  hideEndSessionButton = false,
  isAlreadyMarkedAsCompleted,
  onOpenContentDrawer,
  sessionStarted,
  // Tabs props
  menuItemsList,
  selectedMenuItem,
  setSelectedMenuItem,
  actionButtons,
  therapyReportStatus,
  onSettingsClick,
  onOpenDrawer, // Callback to open drawer for secondary sections
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [sessionStartTimestamp, setSessionStartTimestamp] = useState(null)
  const [currentElapsedTime, setCurrentElapsedTime] = useState(0)

  // Track when session starts to calculate real-time elapsed time
  useEffect(() => {
    if (sessionStarted && !sessionStartTimestamp) {
      // Store the timestamp when session starts, accounting for any pre-existing elapsed time
      const initialTime = timeElapsedInSeconds || 0
      // Set start timestamp to current time minus the initial elapsed time
      // This ensures continuity if timer was already running
      setSessionStartTimestamp(Date.now() - initialTime * 1000)
      setCurrentElapsedTime(initialTime)
    } else if (!sessionStarted) {
      // Reset when session ends
      setSessionStartTimestamp(null)
      setCurrentElapsedTime(0)
    }
  }, [sessionStarted, sessionStartTimestamp, timeElapsedInSeconds])

  // Calculate real-time elapsed time based on actual start timestamp
  // This ensures the timer doesn't pause when tab is in background
  useEffect(() => {
    if (!sessionStarted || !sessionStartTimestamp) {
      return
    }

    const updateTimer = () => {
      const now = Date.now()
      // Calculate elapsed time from the actual start timestamp
      const elapsed = Math.floor((now - sessionStartTimestamp) / 1000)
      setCurrentElapsedTime(elapsed)
    }

    // Update immediately
    updateTimer()

    // Update every second - but calculation is based on real time, not intervals
    // So even if intervals pause, the calculation will be correct when resumed
    const intervalId = setInterval(updateTimer, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [sessionStarted, sessionStartTimestamp, setTimeElapsedInSeconds])

  async function leaveSession(markAsCompleted = false) {
    if (setTimeElapsedInSeconds) {
      setTimeElapsedInSeconds(currentElapsedTime)
    }
    await handleLeaveRoom(markAsCompleted)
  }

  const isOverDuration = currentElapsedTime > (bookedEventFinalData?.eventDuration * 60 || 0)

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
      <LSVideoPartHeader>
        <LiveSessionHeaderDetails
          bookedEventFinalData={bookedEventFinalData}
          auxiliaryData={auxiliaryData}
          asHost={userRole === 'mentor'}
        />
        
        {/* Centered Timer - Prominent Display */}
        {sessionStarted && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: { xs: '100px', md: '120px', lg: '130px' },
                width: { xs: '100px', md: '120px', lg: '130px' },
                padding: { xs: '9px 14px', md: '11px 18px' },
                background: isOverDuration
                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.16) 100%)',
                backdropFilter: 'blur(14px) saturate(180%)',
                WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                border: isOverDuration
                  ? '1.5px solid rgba(239, 68, 68, 0.5)'
                  : '1.5px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '14px',
                boxShadow: isOverDuration
                  ? '0 4px 16px rgba(239, 68, 68, 0.35), 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                  : '0 3px 12px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: isOverDuration
                    ? '0 6px 20px rgba(239, 68, 68, 0.4), 0 3px 10px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 5px 16px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                  borderColor: isOverDuration
                    ? 'rgba(239, 68, 68, 0.6)'
                    : 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: '"Orbitron", "Roboto Mono", monospace',
                  fontSize: { xs: '18px', md: '22px', lg: '24px' },
                  fontWeight: 700,
                  letterSpacing: { xs: '2px', md: '3px' },
                  color: '#ffffff',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {getMmssString(currentElapsedTime)}
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'right', 
          gap: { xs: 1, md: 1.5 }, 
          alignItems: 'center',
          flexShrink: 0,
        }}>
          {/* Mobile: Content Drawer Button (Therapy Report/Consultation/Prescription) */}
          {isMobile && onOpenContentDrawer && (
            <Tooltip title="Open Session Details" arrow>
              <IconButton
                size="small"
                onClick={onOpenContentDrawer}
                sx={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px) saturate(150%)',
                  WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  padding: '8px',
                  width: { xs: '36px', md: '40px' },
                  height: { xs: '36px', md: '40px' },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DescriptionIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
              </IconButton>
            </Tooltip>
          )}
          {!hideEndSessionButton && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'right',
                gap: '10px',
                flexShrink: 0,
                alignItems: 'center',
              }}
            >
              <LeaveButton
                leaveSession={(markAsCompleted = false) => {
                  leaveSession(markAsCompleted)
                }}
                isAlreadyMarkedAsCompleted={isAlreadyMarkedAsCompleted}
              >
                Leave {displayNamesOfEventTypes?.[bookedEventFinalData?.eventType] || 'Session'}
              </LeaveButton>
            </div>
          )}

          {window.innerWidth < 768 && (
            <>
              <MenuIcon
                style={{ cursor: 'pointer' }}
                onClick={(event) => setAnchorElForHeaderMenu(event.currentTarget)}
              />
              <Menu
                anchorEl={anchorElForHeaderMenu}
                id="account-menu"
                open={Boolean(anchorElForHeaderMenu)}
                onClose={() => setAnchorElForHeaderMenu(null)}
                onClick={() => setAnchorElForHeaderMenu(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={() => {
                    setDrawerForMoreOptionsOpenMobile(true)
                  }}
                >
                  More options
                </MenuItem>
                <MenuItem onClick={() => leaveSession()}>End Session</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </LSVideoPartHeader>
      
      {/* Tabs integrated into header */}
      {menuItemsList && (
        <SessionTopTabs
          menuItemsList={menuItemsList}
          selectedMenuItem={selectedMenuItem}
          setSelectedMenuItem={setSelectedMenuItem}
          onOpenContentDrawer={onOpenContentDrawer}
          actionButtons={actionButtons}
          therapyReportStatus={therapyReportStatus}
          onSettingsClick={onSettingsClick}
          onOpenDrawer={onOpenDrawer}
        />
      )}
    </Box>
  )
}
